import { Draft } from 'immer';
import { cloneDeep } from 'lodash';
import { v4 as uuidV4 } from 'uuid';

import {
  typeguards as commonTypeguards,
  finleyAssert,
} from '@finley-technologies/common';

import { FIXED_PRICING_OPTION_KEY } from '../pricing-options/dataMappings';
import { loanKeyByConvention } from './[commitmentId]/dataMappings/setFormValues';
import { CommitmentFormData } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/AddCommitmentModal';
import { CreditAgreementFullArtifact } from '~/core/routes/settings/implementations/common/types';

export const addCommitment = (
  draft: Draft<CreditAgreementFullArtifact>,
  values: CommitmentFormData,
  newKey: string,
) => {
  const lenders = draft.facilityMechanics.parties.capitalProviders.map(
    ({ entity }) => ({ ...entity }),
  );

  // Set defaults if loanMechanicsV1 doesn't exist
  draft.loanMechanicsV1 = draft.loanMechanicsV1 || {
    pricingOptions: [],
    loans: [],
    commitments: [],
  };

  // TODO: this type is getting a refactor, but for now:
  // - The commitment needs to have at least one loan on it, because that's where we store the
  //   information about per-lender commitment amounts.
  // - That loan needs to reference a top level loan. Add one if it doesn't exist.
  // - The top level loan references a pricing option. Add that if it doesn't exist.
  if (draft.loanMechanicsV1.pricingOptions.length === 0) {
    draft.loanMechanicsV1.pricingOptions.push({
      type: 'fixed',
      key: FIXED_PRICING_OPTION_KEY,
      name: 'Fixed',
      invoiceMechanics: { single: [], recurring: [] },
      value: 0,
      margin: { type: 'fixed', value: 0 },
    });
  }
  const pricingOption = draft.loanMechanicsV1.pricingOptions[0];

  let loanForPricingOption = draft.loanMechanicsV1.loans.find(
    (l) => l.pricingOptionKey === pricingOption.key,
  );
  if (!loanForPricingOption) {
    loanForPricingOption = {
      key: loanKeyByConvention({ pricingOptionKey: pricingOption.key }),
      name: loanKeyByConvention({ pricingOptionKey: pricingOption.key }),
      pricingOptionKey: pricingOption.key,

      // TODO: avoid these hardcoded values.
      interest: { calculation: { dayCountConvention: '30 / 360' } },
      maturity: { type: 'months', count: 24 },
    };
    draft.loanMechanicsV1.loans.push(loanForPricingOption);
  }

  finleyAssert(
    commonTypeguards.isFullDateStr(
      draft.facilityMechanics.finalMaturityDate.value,
    ),
    `Expected draft.facilityMechanics.finalMaturityDate.value to be FullDateStr, but got ${draft.facilityMechanics.finalMaturityDate.value}`,
  );

  draft.loanMechanicsV1.commitments.push({
    key: newKey,
    name: 'Untitled Commitment',
    limit: {
      type: 'amount',
      amount: 0,
      currency: 'USD',
      sublimit: values.isSublimit === 'yes',
    },
    loans: [
      {
        // TODO: remove "needs review" once we no longer need hardcoded values
        name: 'Placeholder loan (needs review)',
        loanKey: loanForPricingOption.key,
        lenders: lenders.map((entity) => ({
          entity,
          // This `amount` is the primary reason we needed to stand up all these entities.
          // There is no other place this state can be persisted.
          amount: 0,
        })),
      },
    ],
    maturityDate: draft.facilityMechanics.finalMaturityDate.value,
  });
};

export const duplicateCommitment = (
  draft: Draft<CreditAgreementFullArtifact>,
  commitmentKey: string,
  newName: string,
) => {
  if (draft.loanMechanicsV1) {
    const commitmentToDuplicate = draft.loanMechanicsV1.commitments.find(
      (commitment) => commitment.key === commitmentKey,
    );

    if (commitmentToDuplicate) {
      const newCommitment = cloneDeep(commitmentToDuplicate);
      newCommitment.name = newName;
      newCommitment.key = uuidV4();
      draft.loanMechanicsV1.commitments.push(newCommitment);
    }
  }
};

export const deleteCommitment = (
  draft: Draft<CreditAgreementFullArtifact>,
  commitmentKey: string,
) => {
  if (draft.loanMechanicsV1) {
    draft.loanMechanicsV1.commitments = draft.loanMechanicsV1.commitments.filter(
      (commitment) => commitment.key !== commitmentKey,
    );
  }
};
